<script setup lang="ts">
import type { Component, Document, Page } from '@bloomreach/spa-sdk'
import type { TRefPage } from '@/components/PlusBlog/type'
import type { TBLogPostItem, TCategoryItem } from '@/ts/interfaces/BlogCategory'
import type { TPageElement } from '@/ts/interfaces/BlogPagination'
import type { IBlogList } from '@/ts/interfaces/BlogRequest'

const props = defineProps<{
    component: Component
    page: Page
}>()

const arrayBlogPostToShow = ref<TBLogPostItem[]>([])
const selectedCategories = ref<TCategoryItem[]>([])
const upadatePaginationSerchParams = ref('')

const { component, page } = toRefs(props)
const dataPage = page.value.toJSON()
const models = component?.value.getModels()
const { pagination, categoriesFilter, countLabel, searchbox, searchform, archive } = models || {}
const setCountLabel = ref(countLabel)
const noResults = ref()
const refPage = useExtractPage(pagination?.$ref)

const isTagPage = JSON.stringify(page.value.toJSON()).includes('"ctype":"Tag"')

const contentDataPagePagination = refPage ? dataPage?.page[refPage] : {}

const categoryUrlParam = computed(() => {
    if (selectedCategories.value?.length) {
        return selectedCategories.value
            .map((cat) => `&categoryUrlParam=${encodeURIComponent(cat.categoryUrlParam)}`)
            .join('&')
    }
    return ''
})
const previousWithCategory = computed(() => {
    return updateLinks(contentDataPagePagination.previous)
})
const nextWithCategory = computed(() => {
    return updateLinks(contentDataPagePagination.next)
})
const pagesWithCategory = computed(() => {
    if (contentDataPagePagination.pages?.length) {
        return contentDataPagePagination.pages.map((page: TPageElement) => {
            return updateLinks(page)
        })
    }
    return null
})
const paginationData = computed(() => ({
    pages: pagesWithCategory,
    previous: previousWithCategory,
    next: nextWithCategory,
    current: contentDataPagePagination.current,
}))

function removeCategoryParams(url: string) {
    if (searchform) {
        return
    }
    let currentUrl = url
    const regex = /([?&])categoryUrlParam=[^&]*(?=&|$)/g
    currentUrl = currentUrl.replace(regex, (_match, p1) => {
        return p1 === '?' ? '?' : ''
    })
    currentUrl = currentUrl.replace(/[?&]$/, '')
    return currentUrl
}

function updateLinks(paginationData: TPageElement) {
    const urlParams = ref()
    if (selectedCategories.value?.length) {
        urlParams.value = categoryUrlParam.value
    }
    if (searchform) {
        urlParams.value = `&${upadatePaginationSerchParams.value}`
    }
    if (paginationData) {
        return {
            number: paginationData.number,
            links: {
                self: {
                    href:
                        selectedCategories.value?.length || searchform
                            ? useRemoveDuplicateQueryParams(paginationData.links.self.href + urlParams.value)
                            : removeCategoryParams(paginationData.links.self.href),
                    type: paginationData.links.self.type,
                },
                site: {
                    href:
                        selectedCategories.value?.length || searchform
                            ? useRemoveDuplicateQueryParams(paginationData.links.site.href + urlParams.value)
                            : removeCategoryParams(paginationData.links.self.href),
                    type: paginationData.links.site.type,
                },
            },
        }
    }
    return null
}

/**
 * Extracts post data from the given page object.
 */
function extractPostData(page: IBlogList): TBLogPostItem[] {
    return page?.models?.pagination?.items.map((el: any) => ({
        url: el?.url || '#',
        src: el?.mainImage?.original?.links?.site?.href ?? el.mainImage?.desktop?.links?.site?.href,
        alt: el?.mainImage?.altText ?? '',
        categories: el?.categories?.join(', ') || [],
        publicationdate: el?.publicationdate || '',
        title: el?.title || '',
        id: el?.id,
    }))
}

/**
 * Updates the blog post.
 */
function updateBlogPosts(dataPage: IBlogList) {
    if (!dataPage.models.pagination) {
        arrayBlogPostToShow.value.length = 0
        return
    }
    const posts = extractPostData(dataPage)
    arrayBlogPostToShow.value.length = 0
    arrayBlogPostToShow.value.push(...posts)
}

function updatePageParameter(url: string, newPage: number): string {
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
        url = `https://update.com${url}`
    }
    const urlObj = new URL(url)
    const params = Array.from(urlObj.searchParams.entries())
    const pageParam = params.find(([key]) => key.includes(':page'))
    if (pageParam) {
        const [key] = pageParam
        urlObj.searchParams.set(key, newPage.toString())
    }
    return urlObj.toString().replace(/^https?:\/\/[^/]+/, '')
}

function updatePaginationPosts(dataPage: IBlogList) {
    const paginationData = dataPage?.models?.pagination
    const nextPage = paginationData?.next?.number
    const lastPage = paginationData?.last?.number

    /**
     * current
     */
    contentDataPagePagination.current.number = paginationData?.current?.number
    contentDataPagePagination.current.links.self.href = updatePageParameter(
        contentDataPagePagination.current.links.self.href,
        1,
    )
    contentDataPagePagination.current.links.site.href = updatePageParameter(
        contentDataPagePagination.current.links.site.href,
        1,
    )
    /**
     * previous
     */
    contentDataPagePagination.previous = paginationData?.previous

    /**
     * next
     */
    if (nextPage) {
        contentDataPagePagination.next = { ...contentDataPagePagination.current }
        contentDataPagePagination.next.links.self.href = updatePageParameter(
            contentDataPagePagination.current.links.self.href,
            nextPage,
        )
        contentDataPagePagination.next.links.site.href = updatePageParameter(
            contentDataPagePagination.current.links.site.href,
            nextPage,
        )
    } else {
        contentDataPagePagination.next = nextPage
    }

    /**
     * pages
     */
    if (contentDataPagePagination.pages.length > lastPage) {
        contentDataPagePagination.pages.splice(lastPage)
    } else if (contentDataPagePagination.pages.length < lastPage) {
        for (let i = contentDataPagePagination.pages.length + 1; i <= lastPage; i++) {
            contentDataPagePagination.pages.value = []
            contentDataPagePagination.pages.push({
                number: i,
                links: {
                    self: {
                        href: updatePageParameter(contentDataPagePagination.current.links.self.href, i),
                        type: 'external',
                    },
                    site: {
                        href: updatePageParameter(contentDataPagePagination.current.links.site.href, i),
                        type: 'internal',
                    },
                },
            })
        }
    }
}
function updateSearchDataPagination(url: string) {
    const newUrl = useRemoveDuplicateQueryParams(url)
    const queryString = newUrl.split('?')[1]
    const queryPos = queryString.indexOf('query=')

    if (queryPos === -1) {
        return ''
    }
    upadatePaginationSerchParams.value = queryString.slice(queryPos)
}

/**
 * Fetch posts from a given URL
 */
async function fetchPosts(url: string) {
    try {
        // updatePaginationLink()
        const response = await fetch(url)
        if (!response.ok) {
            throw new Error(`Błąd HTTP: ${response.status}`)
        }
        const data = await response.json()
        if (!data || !data.page) {
            throw new Error('Brak danych do wyświetlenia.')
        }
        const dataPage = data.page[Object.keys(data.page)[0]]
        updateBlogPosts(dataPage)
        updatePaginationPosts(dataPage)
        updateSearchDataPagination(url)
        setCountLabel.value = dataPage?.models?.countLabel
        noResults.value = dataPage?.models?.noResults
    } catch (error) {
        console.error('Wystąpił problem z pobieraniem postów:', error)
    }
}

arrayBlogPostToShow.value = contentDataPagePagination.items?.map((data: TRefPage) => {
    const documentPost = computed(() => page.value.getContent<Document>(data?.$ref))
    const dataPost = computed((): any => documentPost.value?.getData())
    const { title, categories, publicationdate, id, mainImage, url } = dataPost.value
    const documentPostImg = mainImage?.$ref && page.value.getContent<Document>(mainImage?.$ref)
    const postImg = useImageRef(documentPostImg)
    return {
        url,
        src: postImg?.value?.src,
        alt: postImg?.value?.alt,
        categories: categories?.join(', ') || [],
        publicationdate,
        title,
        id,
    }
})
</script>

<template>
    <section>
        <div
            class="mx-auto mb-2 flex max-w-6.5xl flex-col-reverse justify-between gap-6 p-3 md:flex-row md:gap-4 xl:p-0"
        >
            <PlusBlogCategorySet
                v-if="categoriesFilter"
                v-model:categories-selected="selectedCategories"
                :category-select="categoriesFilter"
                @posts="fetchPosts"
            />
            <PlusBlogSearchInput
                v-if="searchbox"
                :url="searchbox?.url"
            />
        </div>
        <div class="mx-auto mb-2 max-w-6.5xl gap-6 p-3 md:gap-4">
            <a
                v-if="archive"
                :href="archive.url"
                target="_blank"
                class="flex justify-end text-plus-blue-700 underline"
            >
                {{ archive.title }}
            </a>
        </div>
        <PlusBlogSearchPage
            v-if="searchform"
            :search-form="searchform"
            @search="fetchPosts"
        />
        <section class="mx-auto max-w-6.5xl p-3 xl:p-0">
            <p
                v-if="countLabel && (isTagPage || searchform)"
                class="mb-4 text-sm md:mb-6 md:text-base"
                v-html="setCountLabel"
            />
            <div class="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                <PlusBlogListItem
                    v-for="el in arrayBlogPostToShow"
                    :key="el.id"
                    :data="el"
                />
            </div>
            <section
                v-if="noResults"
                class="mt-12 text-center"
            >
                <p
                    class="text-2md"
                    v-html="noResults?.title"
                />
                <p
                    class="text-2xl"
                    v-html="noResults?.description"
                />
            </section>
        </section>
        <PlusBlogPagination
            class="mb-[100px] mt-9 md:mt-[60px] lg:mt-[90]"
            :pagination="paginationData"
        />
    </section>
</template>
